import { useEffect, useState } from 'react';
import { Loader2, UserCircle2, UserCog, Trash2, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  removeWorkspaceMember,
  updateMemberRole,
} from '@/lib/database';
import type { Member } from './SuperPanel';

type Role = 'admin' | 'reader';

interface EditMemberDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
  workspaceName: string;
  member: Member | null;
  totalAdmins: number;
  onUpdated: () => void | Promise<void>;
}

export function EditMemberDialog({
  open,
  onOpenChange,
  workspaceId,
  workspaceName,
  member,
  totalAdmins,
  onUpdated,
}: EditMemberDialogProps) {
  const [role, setRole] = useState<Role>('reader');
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);

  useEffect(() => {
    if (open && member) {
      setRole(member.role === 'admin' ? 'admin' : 'reader');
      setError(null);
      setConfirmRemove(false);
    }
  }, [open, member]);

  if (!member) return null;

  const isLastAdmin = member.role === 'admin' && totalAdmins <= 1;
  const roleChanged = role !== member.role;
  const busy = saving || removing;

  const handleSave = async () => {
    if (!roleChanged) {
      onOpenChange(false);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await updateMemberRole(workspaceId, member.user_id, role);
      onOpenChange(false);
      await onUpdated();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update role');
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    if (!confirmRemove) {
      setConfirmRemove(true);
      return;
    }
    setRemoving(true);
    setError(null);
    try {
      await removeWorkspaceMember(workspaceId, member.user_id);
      onOpenChange(false);
      await onUpdated();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove member');
    } finally {
      setRemoving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserCog className="h-5 w-5" />
            Modify member
          </DialogTitle>
          <DialogDescription>
            Update {member.email}'s role in {workspaceName}, or remove them
            from the workspace.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="flex items-center gap-3 px-3 py-3 rounded-md border bg-gray-50">
            <UserCircle2 className="h-5 w-5 text-gray-400 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{member.email}</div>
              <div className="text-xs text-gray-500">
                Joined {new Date(member.joined_at).toLocaleDateString()}
              </div>
            </div>
            <span
              className={`px-2 py-0.5 rounded text-xs ${
                member.role === 'admin'
                  ? 'bg-purple-100 text-purple-800'
                  : 'bg-blue-100 text-blue-800'
              }`}
            >
              {member.role}
            </span>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Role</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setRole('reader')}
                disabled={isLastAdmin}
                className={`px-3 py-2 rounded-md border text-sm text-left transition-colors ${
                  role === 'reader'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:bg-gray-50'
                } ${isLastAdmin ? 'opacity-60 cursor-not-allowed' : ''}`}
              >
                <div className="font-medium">Reader</div>
                <div className="text-xs text-gray-500">View-only access</div>
              </button>
              <button
                type="button"
                onClick={() => setRole('admin')}
                className={`px-3 py-2 rounded-md border text-sm text-left transition-colors ${
                  role === 'admin'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                <div className="font-medium">Admin</div>
                <div className="text-xs text-gray-500">
                  Manage members, billing, projects
                </div>
              </button>
            </div>
            {isLastAdmin && (
              <p className="text-xs text-amber-700 mt-2">
                This is the last admin of the workspace. Promote another
                member to admin before changing or removing them.
              </p>
            )}
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
              {error}
            </div>
          )}
        </div>

        <DialogFooter className="flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
          <Button
            variant="ghost"
            className="text-red-600 hover:text-red-700 hover:bg-red-50"
            onClick={handleRemove}
            disabled={busy || isLastAdmin}
          >
            {removing ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Trash2 className="h-4 w-4 mr-2" />
            )}
            {confirmRemove ? 'Confirm remove' : 'Remove from workspace'}
          </Button>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={busy}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={busy || !roleChanged}
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving…
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save changes
                </>
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
